import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { ProgressState } from "./progress-state";
import type { ProgressMutation } from "./progress-mutation";
import type {
  PlayerFixedStepListener,
  PlayerRuntimeSnapshot,
} from "./player-runtime";
import { firstRunGuidanceConfig } from "../config/first-run-guidance-config";

export interface FirstRunHintProgressStore {
  getState(): ProgressState;
  update(
    createNextState: (state: ProgressState) => ProgressState,
  ): ProgressMutation<boolean>;
}

export interface FirstRunHintPlayer {
  getSnapshot(): Pick<
    PlayerRuntimeSnapshot,
    "foodCollected" | "simulationTimeMs"
  >;
  subscribeFixedStepCompletion(listener: PlayerFixedStepListener): () => void;
}

export interface FirstRunHintSnapshot {
  readonly dismissed: boolean;
  readonly stage: "growth" | "hidden" | "steering";
  readonly visible: boolean;
}

export type FirstRunHintListener = (snapshot: FirstRunHintSnapshot) => void;

export class FirstRunHint {
  private completionPending = false;
  private growthHintFoodCount = 0;
  private growthHintStartedAtMs = 0;
  private readonly listeners = new Set<FirstRunHintListener>();
  private stage: FirstRunHintSnapshot["stage"] = "hidden";
  private readonly unsubscribers: readonly (() => void)[];

  public constructor(
    flow: GameFlow,
    private readonly progressStore: FirstRunHintProgressStore,
    private readonly player?: FirstRunHintPlayer,
  ) {
    this.unsubscribers = [
      flow.subscribe((snapshot) => {
        this.applyFlow(snapshot);
      }),
      ...(player === undefined
        ? []
        : [
            player.subscribeFixedStepCompletion(() => {
              this.applyPlayerStep();
            }),
          ]),
    ];
  }

  public destroy(): void {
    for (const unsubscribe of this.unsubscribers) {
      unsubscribe();
    }
    this.listeners.clear();
  }

  public dismiss(): ProgressMutation<boolean> {
    if (this.stage === "hidden" || this.completionPending) {
      return false;
    }
    this.completionPending = true;
    const saved = this.progressStore.update((state) => ({
      ...state,
      firstRunHintDismissed: true,
    }));
    const finish = (durable: boolean): boolean => {
      this.completionPending = false;
      if (durable) {
        this.stage = "hidden";
        this.notify();
      }
      return durable;
    };
    if (saved instanceof Promise) {
      return saved.then(finish, () => finish(false));
    }
    return finish(saved);
  }

  public completeSteering(): boolean {
    if (this.stage !== "steering") {
      return false;
    }
    const player = this.player?.getSnapshot();
    this.growthHintFoodCount = player?.foodCollected ?? 0;
    this.growthHintStartedAtMs = player?.simulationTimeMs ?? 0;
    this.stage = "growth";
    this.notify();
    return true;
  }

  public getSnapshot(): FirstRunHintSnapshot {
    return {
      dismissed: this.progressStore.getState().firstRunHintDismissed,
      stage: this.stage,
      visible: this.stage !== "hidden",
    };
  }

  public subscribe(listener: FirstRunHintListener): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private applyFlow(snapshot: GameFlowSnapshot): void {
    this.stage =
      snapshot.scene === "gameplay" &&
      !this.progressStore.getState().firstRunHintDismissed
        ? "steering"
        : "hidden";
    this.notify();
  }

  private applyPlayerStep(): void {
    if (this.stage !== "growth" || this.player === undefined) {
      return;
    }
    const snapshot = this.player.getSnapshot();
    const collectedFood = snapshot.foodCollected > this.growthHintFoodCount;
    const timedOut =
      snapshot.simulationTimeMs - this.growthHintStartedAtMs >=
      firstRunGuidanceConfig.growthHintTimeoutMs;
    if (collectedFood || timedOut) {
      void this.dismiss();
    }
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.getSnapshot());
    }
  }
}
